import React, { createContext, ReactNode, useContext, useState } from 'react';
import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'framer-motion';
import Hint from '../components/core/Hint/Hint';

const defaultNotificationDuration = 5000; // ms

export interface Notification {
  showNotification: (text: string, duration?: number) => void;
  clearNotification: () => void;
}

const NotificationContext = createContext<Notification>({
  showNotification: () => {},
  clearNotification: () => {},
});

const styles = {
  hintContainer: css`
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 99;
    display: flex;
    justify-content: center;

    & > div {
      width: fit-content;
      margin-top: 1rem;
    }
  `,
};

export function useNotification() {
  return useContext(NotificationContext);
}

export default function NotificationProvider({ children }: { children: ReactNode }) {
  const [displayNotification, setDisplayNotification] = useState<boolean>(false);
  const [notificationTimeout, setNotificationTimeout] = useState<NodeJS.Timeout | null>(null);
  const [notificationText, setNotificationText] = useState<string>('');

  const clearNotification = () => {
    setDisplayNotification(false);
  };

  const showNotification = (text: string, duration: number = defaultNotificationDuration) => {
    setNotificationText(text);
    if (notificationTimeout) {
      clearTimeout(notificationTimeout);
    }
    const timeout = setTimeout(clearNotification, duration);
    setNotificationTimeout(timeout);
    setDisplayNotification(true);
  };

  return (
    <NotificationContext.Provider value={{ showNotification, clearNotification }}>
      <AnimatePresence>
        {displayNotification && (
          <motion.div
            css={styles.hintContainer}
            initial={{ transform: 'translate(0, -100%)' }}
            animate={{ transform: 'translate(0, 0)' }}
            exit={{ transform: 'translate(0, -100%)' }}
          >
            <div>
              <Hint level="highlight" description={notificationText} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {children}
    </NotificationContext.Provider>
  );
}
